// /src/contexts/AuthContext.jsx
// Global authentication state management
// Provides auth state and modal control for protected mutations

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { configureAuthState } from '../lib/protectedSupabase';

const AuthContext = createContext(null);

// THE "signed in" test. A logged-out visitor is NOT null: App.js installs a truthy guest object
// ({ id: null, display_name: 'Guest', ... }) on every fresh logged-out load, and only an in-session
// logout sets null — so the truthiness of currentUser/authUser is never an authentication test.
// The real user id is the fact. Pure, so prop-fed components can use it without the context.
// Brief: docs/briefs/2026-09-26-guest-object-signed-in-test.md.
export const isSignedIn = (user) => !!user?.id;

export const AuthProvider = ({
  children,
  // External auth state from App.js (for backward compatibility)
  externalIsLoggedIn,
  externalCurrentUser
}) => {
  // Auth state - use external values if provided, otherwise use internal state
  const [internalIsLoggedIn, setInternalIsLoggedIn] = useState(false);
  const [internalCurrentUser, setInternalCurrentUser] = useState(null);

  // Use external values if provided (from App.js), otherwise use internal state
  const isLoggedIn = externalIsLoggedIn !== undefined ? externalIsLoggedIn : internalIsLoggedIn;
  const currentUser = externalCurrentUser !== undefined ? externalCurrentUser : internalCurrentUser;
  const setIsLoggedIn = setInternalIsLoggedIn;
  const setCurrentUser = setInternalCurrentUser;

  // Auth modal state for mutation blocking
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');
  const [pendingAction, setPendingAction] = useState(null);

  // Configure protected Supabase wrapper when auth state changes
  useEffect(() => {
    configureAuthState({
      isLoggedIn,
      currentUserId: currentUser?.id,
      showAuthModal: (message) => {
        setAuthModalMessage(message || 'gate_body_generic');
        setShowAuthModal(true);
      }
    });
  }, [isLoggedIn, currentUser]);

  // Check if user is authenticated (returns true only if actually logged in)
  const isAuthenticated = useCallback(() => {
    return isLoggedIn && isSignedIn(currentUser);
  }, [isLoggedIn, currentUser]);

  // Show auth modal with custom message (for protected mutations)
  // Returns true if authenticated, false if modal was shown
  const requireAuth = useCallback((message = 'gate_body_generic', action = null) => {
    // Check if user is actually authenticated
    const authenticated = isLoggedIn && isSignedIn(currentUser);
    if (authenticated) {
      if (action) action();
      return true;
    }
    setAuthModalMessage(message);
    setPendingAction(() => action);
    setShowAuthModal(true);
    // Dispatch event for App.js to open the login modal (carrying the gate message)
    window.dispatchEvent(new CustomEvent('ournet:showAuthRequired', {
      detail: { message }
    }));
    return false;
  }, [isLoggedIn, currentUser]);

  // Handle successful login
  const handleLoginSuccess = useCallback((user) => {
    console.log('AuthContext: Login successful for', user.email);
    setCurrentUser({
      id: user.id,
      display_name: user.display_name,
      email: user.email
    });
    setIsLoggedIn(true);
    setShowAuthModal(false);

    // Refresh user-specific data (areas/localities in header) with small delay
    // to ensure Supabase session is fully propagated
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));
    }, 100);

    // Execute pending action if any
    if (pendingAction) {
      console.log('AuthContext: Executing pending action after login');
      pendingAction();
      setPendingAction(null);
    }
  }, [pendingAction]);

  // Handle logout
  const handleLogout = useCallback(() => {
    console.log('AuthContext: User logged out');
    setCurrentUser(null);
    setIsLoggedIn(false);
    localStorage.removeItem('ournet_saved_email');
    localStorage.removeItem('ournet_saved_password');

    // Refresh user-specific data (areas/localities in header) with small delay
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('ournet:refresh-locations'));
    }, 100);
  }, []);

  // Close modal without login ("Carry on browsing")
  const closeAuthModal = useCallback(() => {
    setShowAuthModal(false);
    setPendingAction(null);
    setAuthModalMessage('');
  }, []);

  // Go to login (close auth modal, trigger login modal)
  const goToLogin = useCallback(() => {
    setShowAuthModal(false);
    // Dispatch event for App.js to catch and show LoginRegisterModal
    window.dispatchEvent(new CustomEvent('ournet:show-login'));
  }, []);

  const value = {
    // State
    isLoggedIn,
    currentUser,
    showAuthModal,
    authModalMessage,

    // State setters (for App.js compatibility during migration)
    setIsLoggedIn,
    setCurrentUser,

    // Actions
    requireAuth,
    isAuthenticated,
    handleLoginSuccess,
    handleLogout,
    closeAuthModal,
    goToLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
// Returns safe fallback when used outside AuthProvider (e.g., in display/preview components)
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return safe fallback for components that may be rendered outside AuthProvider
    return {
      isLoggedIn: false,
      currentUser: null,
      requireAuth: () => false,
      isAuthenticated: () => false,
      showAuthModal: false,
      setShowAuthModal: () => {},
      authModalMessage: '',
      setIsLoggedIn: () => {},
      setCurrentUser: () => {},
      closeAuthModal: () => {},
      goToLogin: () => {},
      executeAfterAuth: () => {}
    };
  }
  return context;
};

export default AuthContext;
